import { Box, Stack, Typography } from '@mui/material'
import SectionContainer from './SectionContainer'
import { SectionTemplate } from '../types'
import CompareBars from '../charts/CompareBars'
import { pathColor } from '../palette'

interface Range {
  low: number
  high: number
  currency?: string
}

interface Path {
  title?: string
  baseComp?: Range
  longTermComp?: Range
}

interface Props {
  template: SectionTemplate
  values: { paths?: Path[] }
  title: string
  description: string
  sectionNumber?: number
  disclaimer?: string
}

function rangeLabel(r?: Range) {
  if (!r) return '—'
  const c = r.currency || '$'
  const sym = c === 'USD' ? '$' : c
  return `${sym}${Math.round((r.low || 0) / 1000)}k – ${sym}${Math.round((r.high || 0) / 1000)}k`
}

export default function ChartCompareSection({
  template,
  values,
  title,
  description,
  sectionNumber,
  disclaimer,
}: Props) {
  const paths = values?.paths || []

  // Use short "Path N" labels on the chart; full titles are already shown in
  // the big-number cards above and in the tooltip.
  const chartData = paths.map((p, i) => ({
    name: `Path ${i + 1}`,
    fullName: p.title || `Path ${i + 1}`,
    Base: p.baseComp?.high || 0,
    LongTerm: p.longTermComp?.high || 0,
  }))

  return (
    <SectionContainer
      id={template.sectionTypeId}
      number={sectionNumber}
      title={title}
      description={description}
    >
      <Stack spacing={5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: `repeat(${Math.min(paths.length, 3)}, 1fr)`,
            },
            gap: 3,
          }}
        >
          {paths.map((path, i) => {
            const accent = pathColor(i)
            return (
              <Box
                key={i}
                sx={{
                  p: 3,
                  background: 'rgba(255,255,255,0.7)',
                  borderLeft: `4px solid ${accent}`,
                  borderTop: 1,
                  borderRight: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ minHeight: { md: 72 } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: accent,
                      letterSpacing: 2,
                      fontWeight: 700,
                      display: 'block',
                      lineHeight: 1.4,
                    }}
                  >
                    {path.title}
                  </Typography>
                </Box>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 2 }}>
                      Base
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'var(--ns-display-font)',
                        fontSize: 26,
                        fontWeight: 500,
                        color: '#1a1a1a',
                      }}
                    >
                      {rangeLabel(path.baseComp)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 2 }}>
                      Long-term
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'var(--ns-display-font)',
                        fontSize: 26,
                        fontWeight: 500,
                        color: accent,
                      }}
                    >
                      {rangeLabel(path.longTermComp)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )
          })}
        </Box>
        <Box>
          <CompareBars
            data={chartData}
            series={[
              { key: 'Base', label: 'Base (high)', color: '#5a5a5a' },
              { key: 'LongTerm', label: 'Long-term (high)', color: '#c9954a' },
            ]}
          />
        </Box>
        {disclaimer && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {disclaimer}
          </Typography>
        )}
      </Stack>
    </SectionContainer>
  )
}
